import dayjs from 'dayjs';
import { DAYS_OFFSET_OBJ } from '../constants/oneDayCompareWidget.constants';

export const getYesterdayDate = (today: Date): Date => {
  return dayjs(today).subtract(1, 'day').toDate();
};

export const isSameDate = (date1: Date, date2: Date): boolean => {
  const format = 'YYYY-MM-DD';
  return dayjs(date1).format(format) === dayjs(date2).format(format);
};

export const getMondayOfWeek = (date: Date): dayjs.Dayjs => {
  const currentDay = dayjs(date).day();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  return dayjs(date).add(mondayOffset, 'day').startOf('day');
};

export const calculateLastDay = (now: Date, value: number): Date => {
  return dayjs(now).subtract(value, 'day').toDate();
};

export const calculateLastMonth = (
  now: Date,
  value: number,
  isFixedDate?: boolean,
  selectedDate?: string,
  fixedValue?: number,
): Date => {
  const baseDate = dayjs(now).subtract(value, 'month');

  if (!isFixedDate) {
    return baseDate.toDate();
  }

  switch (selectedDate) {
    case 'enterDirectly':
      return baseDate.date(fixedValue!).toDate();
    case 'firstDay':
      return baseDate.startOf('month').toDate();
    case 'lastDay':
      return baseDate.endOf('month').toDate();
    default:
      return baseDate.toDate();
  }
};

export const calculateThisMonth = (now: Date, value: number, selectedDate?: string): Date => {
  const baseDate = dayjs(now);

  switch (selectedDate) {
    case 'enterDirectly':
      return baseDate.date(value).toDate();
    case 'firstDay':
      return baseDate.startOf('month').toDate();
    case 'lastDay':
      return baseDate.endOf('month').toDate();
    default:
      return baseDate.startOf('day').toDate();
  }
};

export const calculateLastWeek = (
  now: Date,
  value: number,
  daysOfTheWeek: string,
  isFixedDate?: boolean,
  daysOffsetObj = DAYS_OFFSET_OBJ,
): Date => {
  if (!isFixedDate || !daysOfTheWeek) {
    return dayjs(now).subtract(value, 'week').toDate();
  }

  const targetDayOffset = daysOffsetObj[daysOfTheWeek] ?? 0;
  const targetWeekMonday = getMondayOfWeek(now).subtract(value, 'week');
  return targetWeekMonday.add(targetDayOffset, 'day').toDate();
};

export const calculateThisWeek = (
  now: Date,
  daysOfTheWeek: string,
  daysOffsetObj = DAYS_OFFSET_OBJ,
): Date => {
  const thisWeekMonday = getMondayOfWeek(now);
  const targetDayOffset = daysOffsetObj[daysOfTheWeek] ?? 0;
  return thisWeekMonday.add(targetDayOffset, 'day').toDate();
};

export const isOverToday = (targetDate: Date, today: Date): boolean => {
  const convertTargetDate = dayjs(targetDate).startOf('day');

  return convertTargetDate.isAfter(today) || isSameDate(targetDate, today);
};

// ==================== Formatter Utils ====================

export const getPluralForm = (value: number, singular: string, plural: string): string => {
  return value === 1 ? singular : plural;
};

export type DayOfTheWeekShort = {
  MON: string;
  TUE: string;
  WED: string;
  THU: string;
  FRI: string;
  SAT: string;
  SUN: string;
};

export const getDayOfTheWeekStr = (
  dayOfTheWeek: string,
  dayOfTheWeekShort: DayOfTheWeekShort,
): string => {
  const key = dayOfTheWeek.slice(0, 3).toUpperCase() as keyof DayOfTheWeekShort;
  return dayOfTheWeekShort?.[key] ?? '';
};

export interface FormatLabelConstants {
  COMPARE_LAST: string;
  COMPARE_THIS: string;
  WEEK: string;
  WEEKS: string;
  MONTH: string;
  MONTHS: string;
  DAY: string;
  DAYS: string;
  DAY_OF_THE_WEEK_SHORT: DayOfTheWeekShort;
}

export const formatLastWeekLabel = (
  value: number,
  isFixedDate: boolean | undefined,
  daysOfTheWeek: string | undefined,
  defaultLabel: string,
  constants: FormatLabelConstants,
): string => {
  const weekForm = getPluralForm(value, constants.WEEK, constants.WEEKS);

  if (isFixedDate && daysOfTheWeek) {
    const dayStr = getDayOfTheWeekStr(daysOfTheWeek, constants.DAY_OF_THE_WEEK_SHORT);
    return `${constants.COMPARE_LAST} ${value} ${weekForm}, ${dayStr} (${defaultLabel})`;
  }

  return `${constants.COMPARE_LAST} ${value} ${weekForm}, (${defaultLabel})`;
};

export const formatLastDayLabel = (
  value: number,
  defaultLabel: string,
  constants: FormatLabelConstants,
): string => {
  const dayForm = getPluralForm(value, constants.DAY, constants.DAYS);
  return `${constants.COMPARE_LAST} ${value} ${dayForm} (${defaultLabel})`;
};

export const formatThisWeekLabel = (
  daysOfTheWeek: string,
  defaultLabel: string,
  constants: FormatLabelConstants,
): string => {
  const dayStr = getDayOfTheWeekStr(daysOfTheWeek, constants.DAY_OF_THE_WEEK_SHORT);
  return `${constants.COMPARE_THIS} ${constants.WEEK}, ${dayStr} (${defaultLabel})`;
};
